import {
  HTMLAttributes,
  useRef,
  useLayoutEffect,
  memo,
  ReactElement,
  useState,
  useEffect,
} from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { LoadingOutlined, PictureOutlined } from '@ant-design/icons';
import { useInViewport } from 'ahooks';
import useImageStatus from 'hooks/useImageStatus';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { getImageThumb, getTiktokProxyImageSrc } from 'lib/utils/platformImage';

const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #b5b9bf;
  > span,
  > img {
    font-size: 20px;
  }
  > p {
    margin-bottom: 0;
    font-size: 12px;
  }
`;

const Error = styled(Placeholder)`
  cursor: pointer;
`;

const Image = styled.div<{ src: string }>`
  background: ${({ src }) => `center / cover no-repeat url(${src})`};
`;

export type ImageRender = (src: string, props: HTMLAttributes<HTMLElement>) => ReactElement;

const defaultImageRender: ImageRender = (src, props) => {
  return <Image src={src} {...props} />;
};

export interface StatusImageProps extends HTMLAttributes<HTMLElement> {
  src: string;
  platform?: General.PlatformUpperCase;
  imageRender?: ImageRender;
}

function StatusImage(props: StatusImageProps) {
  const { src, imageRender = defaultImageRender, ...otherProps } = props;

  const { t } = useTranslate();

  const { error, loading, loaded, load } = useImageStatus(src, false);

  const ref = useRef<HTMLDivElement>(null);

  const [isInViewPort] = useInViewport(ref);

  useLayoutEffect(() => {
    if (isInViewPort && !loaded) {
      load();
    }
  }, [load, isInViewPort, loaded]);

  if (loading) {
    return (
      <Placeholder ref={ref} {...otherProps}>
        <LoadingOutlined />
        <p>{t('scene.chatWorkspace.orderTab.preview.status.loading')}</p>
      </Placeholder>
    );
  }

  if (error) {
    return (
      <Error ref={ref} onClick={load} {...otherProps}>
        <PictureOutlined />
        <p>{t('scene.chatWorkspace.orderTab.preview.status.loadFailed')}</p>
      </Error>
    );
  }

  if (loaded) {
    return imageRender(src, otherProps);
  }

  return <div ref={ref} {...otherProps} />;
}

const StatusImageWrapper = (props: StatusImageProps) => {
  const { src, platform, ...otherProps } = props;
  const { tiktokImageProxyOriginList } = useOrgConfigStateValue();

  const [rewriteSrc, setRewriteSrc] = useState('');

  useEffect(() => {
    if (tiktokImageProxyOriginList?.some((origin) => src.includes(origin))) {
      setRewriteSrc(getTiktokProxyImageSrc(src));
    } else {
      setRewriteSrc(getImageThumb(src, platform));
    }
  }, [src, tiktokImageProxyOriginList, platform]);

  return <StatusImage src={rewriteSrc} platform={platform} {...otherProps} />;
};

export default memo(StatusImageWrapper);
